import { useEffect, useMemo, useState, type CSSProperties } from 'react'
import { Award, Check, ChevronRight, Clock3, Lock, ScanQrCode } from 'lucide-react'
import { useNavigate } from 'react-router'
import { api } from '../lib/api'
import type { CertStatus } from '../lib/types'
import { useAuthed } from '../lib/session'
import { useSlots, useTick } from '../features/useSlots'
import { canCheckIn, formatClock, formatDay, groupByDay, slotState, slotWindow } from '../lib/slot-time'
import { Masthead } from '../ui/Chrome'
import { Notice, Skeleton } from '../ui/Feedback'

export function AttendanceRoute() {
  const session = useAuthed()
  const navigate = useNavigate()
  const { slots, totalCheckin, loading, error, refresh } = useSlots()
  const now = useTick(15_000)
  const [cert, setCert] = useState<CertStatus | null>(null)
  const [certLoading, setCertLoading] = useState(true)
  const [certFailed, setCertFailed] = useState(false)

  useEffect(() => {
    let cancelled = false
    setCertLoading(true)
    setCertFailed(false)
    api.certStatus(session.email)
      .then((res) => !cancelled && setCert(res))
      .catch(() => !cancelled && setCertFailed(true))
      .finally(() => !cancelled && setCertLoading(false))
    return () => { cancelled = true }
  }, [session.email])

  const required = useMemo(() => slots.filter((s) => Boolean(s.qr_required)).length, [slots])
  const pct = required ? Math.min(100, Math.round((totalCheckin / required) * 100)) : 0
  /*
   * Exactly the sessions whose check-in is open, which is what `canCheckIn` means -
   * rather than a second, looser rule reimplemented here.
   *
   * The old test was `qr_required && slotState === 'live' && !checkin_status`, which
   * ignores `offset_checkin_minute`. On a 09:00-11:00 session with a 90 minute offset
   * that put a "Check in now" card on this screen from 09:00, routing straight to the
   * scanner, while the same session's row on the Programme read "Opens in 30 min" -
   * two screens disagreeing about whether a thing was possible.
   *
   * It picks up the grace period for free: a session that has run past its end keeps
   * its card here for as long as its check-in is still open.
   */
  const openSessions = useMemo(
    () => slots.filter((s) => canCheckIn(s, now)),
    [slots, now],
  )
  /*
   * Every recorded check-in, grouped by the day it happened on.
   *
   * Not paginated and not truncated. It used to be capped at six rows directly beneath
   * a ring reading 12 / 12, with nothing to say the list had been cut. The record is
   * this screen's whole job and it is bounded anyway: it can never exceed the
   * qr_required slots on the participant's own track, which is 10 to 12 on this event
   * and 15 for the largest event in the database. Day headings are the signposting a
   * list that length needs; pagination would be a control with nothing to do.
   *
   * Newest first throughout - days descending, and sessions descending inside each day,
   * so reading down the page never changes direction.
   */
  const recent = useMemo(
    () => slots.filter((s) => Boolean(s.checkin_status) && Boolean(s.qr_required)),
    [slots],
  )
  const history = useMemo(
    () => groupByDay(recent).slice().reverse()
      .map((day) => ({ ...day, slots: day.slots.slice().reverse() })),
    [recent],
  )

  return (
    <main className="page attendance-page">
      {/*
        * No `sub`. It read "9 of 12 required sessions recorded" directly above a hero
        * saying 75%, 9 / 12, and "required sessions recorded" - one number in four
        * places inside 255px, before the record it describes had started.
        */}
      <Masthead kicker="Your record" title="Attendance" />

      <section className="attendance-hero">
        <div className="attendance-hero__ring" style={{ '--pct': `${pct}%` } as CSSProperties}>
          <span className="tnum">{pct}%</span>
        </div>
        <div>
          {/*
            * No kicker over the figure. "Attendance progress" labelled a ring and a
            * ratio that identify themselves, on a screen whose own title is
            * "Attendance" 60px above.
            *
            * And no verdict line. "You are on track for the certificate" was the third
            * statement of an outcome the certificate card directly below announces
            * properly - that card is where the certificate is explained, and it says so
            * with the state and a way through to it. This block reports the count.
            */}
          <h2 className="attendance-hero__ratio">{totalCheckin} <span>/ {required || '—'}</span></h2>
          <p className="attendance-hero__unit">required sessions recorded</p>
        </div>
      </section>

      {openSessions.length > 0 ? (
        <section className="attendance-section">
          <div className="attendance-section__head">
            <h2 className="display">Check in now</h2>
            <span className="kicker">{openSessions.length} open</span>
          </div>
          <div className="attendance-open-list">
            {openSessions.map((slot) => (
              <button className="attendance-open-card" key={slot.id} onClick={() => navigate(`/scan/${slot.id}`)}>
                <div>
                  {/*
                    * The dot is `--highlight` and this label was `--brand`, so one word
                    * arrived in two signal colours 4px apart. Live is warm everywhere
                    * else in the app; the class carries it now instead of an inline
                    * style reaching for the other one.
                    */}
                  {/*
                    * "Running over" once the session has ended but its check-in is
                    * still open. The card is right to be here - that is what the grace
                    * is for - but calling a room people have left "live now" would be
                    * the app telling a small lie to keep a label simple.
                    */}
                  <span className="live-dot" /> <span className="kicker attendance-open-card__live">
                    {slotState(slot, now) === 'live' ? 'Live now' : 'Running over'}
                  </span>
                  <h3 className="display">{slot.slot_name}</h3>
                  <p><Clock3 size={14} /> {formatClock(slotWindow(slot).start)}–{formatClock(slotWindow(slot).end)}</p>
                  <p>{slot.venue_name}</p>
                </div>
                <ScanQrCode size={21} strokeWidth={1.5} />
              </button>
            ))}
          </div>
        </section>
      ) : null}

      {/*
        * Certificate above the record, not below it.
        *
        * The history is twelve rows on this event, so the certificate sat 1495px
        * down behind 879px of scrolling - the thing the reader came for, filed
        * under the evidence for it. Summary, then outcome, then detail.
        */}
      <section className="attendance-section attendance-cert">
        <div className="attendance-section__head">
          <h2 className="display">Certificate</h2>
          <Award size={20} strokeWidth={1.4} />
        </div>

        {/*
          * 6rem, not 8. The row it stands in for is 76px when the certificate is
          * released and 113px when it is not, so a 128px placeholder collapsed the
          * panel on settle in both cases. Neither height is knowable before the
          * response, so this sits between them and the shift is a few pixels either
          * way rather than a card-sized one.
          */}
        {certLoading ? <Skeleton height="6rem" /> : certFailed || !cert ? (
          <Notice
            icon={<Award size={22} strokeWidth={1.3} />}
            title="Could not check certificate status"
            body="The certificate service did not respond. Your attendance is still being recorded."
          />
        ) : !cert.show_cert ? (
          /*
           * The same row as the released branch below, not a div wrapping an 18px
           * chevron. Both states lead to the same place, so both are the whole row -
           * a bare chevron was a 335px card carrying an 18px target in the corner of
           * it, and the difference between the two branches was accidental rather
           * than meaningful.
           */
          <button
            type="button"
            className="attendance-cert__status"
            onClick={() => navigate('/certificate')}
          >
            <span className="attendance-cert__icon"><Lock size={18} strokeWidth={1.5} /></span>
            <div>
              <strong>Not released yet</strong>
              <span>Keep checking in. The organisers will publish certificates after the event.</span>
            </div>
            <ChevronRight size={18} />
          </button>
        ) : (
          /*
           * A status line, not a second announcement.
           *
           * Home already carries the ready card and the download. This used to repeat
           * both - the same state in a different colour on the adjacent screen, with
           * its own link straight to the PDF. Worse, between the two of them nothing
           * pointed at /certificate any more, and that screen has no tab, so once the
           * certificate was issued the whole screen became unreachable.
           *
           * So this always routes inward now. The PDF opens from Home, or from the
           * Certificate screen this leads to - never from three places at once.
           */
          <button
            type="button"
            className="attendance-cert__status"
            data-ready={cert.cert_eligable || undefined}
            onClick={() => navigate('/certificate')}
          >
            <span className="attendance-cert__icon"><Award size={18} strokeWidth={1.5} /></span>
            <div>
              <strong>{cert.attendance_exempt ? 'Committee' : cert.cert_eligable ? 'Attendance requirement met' : 'Keep going'}</strong>
              <span>
                {cert.attendance_exempt
                  ? cert.cert_url ? 'Issued · attendance not required' : 'Attendance is not required. Your certificate is being prepared.'
                  : cert.cert_eligable
                    ? cert.cert_url
                      ? `Issued · ${totalCheckin} of ${required} sessions`
                      : 'Your certificate is being prepared.'
                    : `${pct}% attendance recorded so far.`}
              </span>
            </div>
            <ChevronRight size={18} />
          </button>
        )}
      </section>

      <section className="attendance-section">
        <div className="attendance-section__head">
          {/* The count reconciles this list against the ring above it at a glance. */}
          <h2 className="display">
            Check-in history
            {recent.length > 0 ? <span className="attendance-section__count tnum"> {recent.length}</span> : null}
          </h2>
          {/* No "View programme" link here: Programme is one of the four tabs and is
              on screen already. There is also nothing "more" of this list to view -
              the heading's own count says it is complete. */}
        </div>
        {loading && slots.length === 0 ? <Skeleton height="12rem" /> : null}
        {!loading && recent.length === 0 ? (
          <Notice
            icon={<Clock3 size={22} strokeWidth={1.3} />}
            title={error ? 'Could not load attendance' : 'No sessions recorded yet'}
            body={error ? 'Check your connection and try again.' : 'Your successful session check-ins will appear here.'}
            action={error ? { label: 'Try again', onClick: refresh } : { label: 'View programme', onClick: () => navigate('/programme') }}
          />
        ) : (
          <div className="attendance-history">
            {history.map((day) => (
              <section className="attendance-day" key={day.key}>
                <h3 className="attendance-day__head">
                  {/* formatDay returns '' for a time that would not parse; the raw
                      label the API sent is the fallback so a day is never unlabelled. */}
                  <span className="kicker">
                    {formatDay(slotWindow(day.slots[0]).start) || day.label}
                  </span>
                  <span className="attendance-day__count tnum">{day.slots.length}</span>
                </h3>
                {day.slots.map((slot) => (
                  <div className="attendance-history__row" key={slot.id}>
                    <span className="attendance-history__mark"><Check size={14} strokeWidth={2} /></span>
                    <div>
                      <strong>{slot.slot_name}</strong>
                      {/* The date moved up to the heading, so the row shows the time
                          instead - which is what distinguishes two rows under one day. */}
                      <span className="tnum">
                        {formatClock(slotWindow(slot).start)} · {slot.venue_name}
                      </span>
                    </div>
                  </div>
                ))}
              </section>
            ))}
          </div>
        )}
      </section>

    </main>
  )
}
